import _ from 'lodash';
import angular from 'angular';
import template from './template.html';
import './style.scss';
import './select-candidate-emails';
import './select-email-template';
import './summary';
import {
    EMAIL_SELECT_MESSAGE_TEMPLATE_STATE,
    EMAIL_SELECT_CANDIDATE_EMAILS,
    EMAIL_SUMMARY_TEMPLATE_STATE,
} from './step-constants';
import MessageFormatter from '../../service/message-formatter';
import { EDIT_STATE, VIEW_STATE } from '../../summernote-options';
import { CONTACT_CHANNEL_TYPES } from '../../../recruiter/constants/contactTypes';

export const UPDATE_TOTAL_CANDIDATES_SUMMARY_TYPE = 'UPDATE_TOTAL_CANDIDATES_SUMMARY_TYPE';
export const INCREMENT_SEND_EMAILS_SUMMARY_TYPE = 'INCREMENT_SEND_EMAILS_SUMMARY_TYPE';

export class EmailModeComponent {
    currentStep = EMAIL_SELECT_MESSAGE_TEMPLATE_STATE;

    candidates = [];

    candidatesEmailList = [];

    publicEmailDomains = [];

    isSelectedCandidatesEmailsValid = false;

    isFormLoading = false;

    emailTextareaEditState = VIEW_STATE;

    templateOptions = [];

    selectedTemplate = [];

    selectedTemplateIndex = 0;

    email = {
        subject: '',
        message: '',
    };

    summary = {
        totalCandidates: 0,
        sendEmails: 0,
    };

    /* @ngInject */
    constructor(
        $timeout,
        utils,
        ModalDialogService,
        RouteService,
        ContactCandidateService,
        CampaignService,
    ) {
        this.$timeout = $timeout;
        this.utils = utils;
        this.ModalDialogService = ModalDialogService;
        this.routeService = RouteService;
        this.contactCandidateService = ContactCandidateService;
        this.campaignService = CampaignService;
    }

    async $onInit() {
        this.loading = true;
        const { result: publicEmailDomains } = await this.routeService.getPublicEmailDomains();
        const templates = await this.getMessageTemplates();
        const templateOptions = this.getMessageTemplateOptions(templates);

        this.$timeout(() => {
            this.publicEmailDomains = publicEmailDomains;
            this.templateOptions = templateOptions;
            this.onEmailTemplateChanged(templateOptions[this.selectedTemplateIndex]);
            this.$onChanges();
        });
        this.loading = false;
    }

    $onChanges() {
        const { candidates, publicEmailDomains } = this;
        const candidatesEmailList = this.getCandidatesEmailList(candidates, publicEmailDomains);

        this.updateSummary(UPDATE_TOTAL_CANDIDATES_SUMMARY_TYPE, candidates.length);
        this.updateCandidatesEmailList(candidatesEmailList);
    }

    async getMessageTemplates() {
        const language = this.routeService.getUserPreference('uiLanguage') || 'en-US';

        return this.contactCandidateService.getMessageTemplatesByContactChannel({
            channel: CONTACT_CHANNEL_TYPES.EMAIL,
            language,
            positionId: this.position._id,
        });
    }

    getMessageTemplateOptions(templates = []) {
        return templates.map((template, index) => ({
            label: template.name,
            value: index,
            template,
        }));
    }

    getCandidatesEmailList(candidates, publicEmailDomains) {
        return candidates.map((candidate) => {
            const emailAddress = _.get(candidate, 'candidateData.contact.emailAddress', []);
            const candidateEmails = (emailAddress || []).map(value => ({ value }));
            const personalEmails = this.getPersonalEmails(candidateEmails, publicEmailDomains);
            const candidateEmailList = {
                candidate,
                emails: [...candidateEmails || []],
                selected: [...personalEmails || []].map(email => ({
                    ...email,
                    canRemove: true,
                })),
            };

            return {
                ...candidateEmailList,
                isValid: this.isCandidateEmailListValid(candidateEmailList),
            };
        });
    }

    isCandidateEmailListValid(candidateEmailList) {
        return candidateEmailList.selected.length > 0;
    }

    isCandidatesEmailListValid(candidatesEmailList) {
        return (candidatesEmailList || []).every(({ isValid }) => isValid);
    }

    updateCandidatesEmailList(candidatesEmailList) {
        this.candidatesEmailList = (candidatesEmailList || []).map(candidateEmailList => ({
            ...candidateEmailList,
            isValid: this.isCandidateEmailListValid(candidateEmailList),
        }));

        this.isSelectedCandidatesEmailsValid = this.isCandidatesEmailListValid(this.candidatesEmailList);
    }

    getPersonalEmails(emails, publicEmailDomains) {
        return (emails || []).reduce((personalEmails, email) => {
            const emailDomain = this.utils.getEmailDomain(email.value) || '';
            const isPublicDomain = this.isPublicEmailDomain(emailDomain, publicEmailDomains);

            if (isPublicDomain) {
                return [
                    ...personalEmails,
                    email,
                ];
            }

            return personalEmails;
        }, []);
    }

    isPublicEmailDomain(emailDomain = '', publicEmailDomains) {
        const emailDomainWithPrefix = `@${emailDomain.toLowerCase()}`;

        return (publicEmailDomains || []).some(publicDomain => (
            emailDomainWithPrefix.indexOf(`.${publicDomain.toLowerCase()}.`) !== -1 ||
            emailDomainWithPrefix.indexOf(`@${publicDomain.toLowerCase()}.`) !== -1
        ));
    }

    setStep(step) {
        this.currentStep = step;
    }

    onCandidateEmailListChanged(changedCandidateEmailList) {
        const candidatesEmailList = this.candidatesEmailList.map((candidateEmailFromList) => {
            const candidateId = _.get(candidateEmailFromList, 'candidate.candidateData._id');
            const changedCandidateId = _.get(changedCandidateEmailList, 'candidate.candidateData._id');
            const isCandidateToChange = candidateId && changedCandidateId && candidateId === changedCandidateId;
            return isCandidateToChange ? changedCandidateEmailList : candidateEmailFromList;
        });

        this.updateCandidatesEmailList(candidatesEmailList);
    }

    onNext() {
        if (!this.isSelectedCandidatesEmailsValid) {
            this.showEmailMissingConfirmationPopup(() => {
                this.setStep(EMAIL_SELECT_MESSAGE_TEMPLATE_STATE);
            });
        } else {
            this.setStep(EMAIL_SELECT_MESSAGE_TEMPLATE_STATE);
        }
    }

    onSelectCandidateEmailsClick() {
        if (this.emailTextareaEditState === EDIT_STATE) {
            this.showMessageOverrideConfirmationAlert(() => {
                this.setStep(EMAIL_SELECT_CANDIDATE_EMAILS);
            });
        } else {
            this.setStep(EMAIL_SELECT_CANDIDATE_EMAILS);
        }
    }

    onEmailTemplateChanged({ template, value: index }) {
        if (this.emailTextareaEditState === EDIT_STATE) {
            this.showMessageOverrideConfirmationAlert(() => {
                this.selectedTemplate = template;
                this.selectedTemplateIndex = index;
                this.mapEmailToTokens();
                this.mapEmailToSpans();
                this.onEmailTextareaEditStateChanged(VIEW_STATE);
            });
        } else {
            this.selectedTemplate = template;
            this.selectedTemplateIndex = index;
            this.mapEmailToTokens();
            this.mapEmailToSpans();
            this.onEmailTextareaEditStateChanged(VIEW_STATE);
        }
    }

    onEmailChange(email) {
        this.email = email;
    }

    startLoadingForm() {
        this.$timeout(() => this.isFormLoading = true);
    }

    stopLoadingForm() {
        this.$timeout(() => this.isFormLoading = false);
    }

    async sendEmail() {
        const { recruiter, position, employerRep } = this;

        this.startLoadingForm();

        await Promise.all((this.candidatesEmailList || []).map(async ({ candidate, selected }) => {
            const { subject, template, name } =
                MessageFormatter.fillTemplate({ ...this.selectedTemplate }, candidate, recruiter, position, undefined, employerRep);

            if (selected.length > 0) {
                this.updateSummary(INCREMENT_SEND_EMAILS_SUMMARY_TYPE);

                return this.onSendEmail({
                    candidate,
                    email: {
                        msgSubject: subject,
                        msgContent: template,
                    },
                    emailAddresses: selected.map(({ value }) => value),
                    selectedMessage: this.selectedTemplate,
                });
            }
        }));

        this.setStep(EMAIL_SUMMARY_TEMPLATE_STATE);

        this.stopLoadingForm();
    }

    updateSummary(type, value) {
        if (type === UPDATE_TOTAL_CANDIDATES_SUMMARY_TYPE) {
            this.summary.totalCandidates = value;
        }

        if (type === INCREMENT_SEND_EMAILS_SUMMARY_TYPE) {
            this.summary.sendEmails++;
        }
    }

    showEmailMissingConfirmationPopup(onConfirm) {
        this.ModalDialogService.showDialog({
            modalDialogId: 'BatchTemplateEmailMissingAlert',
            title: 'MESSAGE_EMAIL_MISSING_ALERT_HEADER',
            body: 'MESSAGE_EMAIL_MISSING_ALERT',
            showCancelButton: true,
            showCloseButton: false,
            customZIndex: 2000,
            onClose: () => {},
            buttons: [
                {
                    label: 'MESSAGE_EMAIL_MISSING_ALERT_OK_BUTTON',
                    className: 'primary-button',
                    onClick: onConfirm,
                },
            ],
        });
    }

    showMessageOverrideConfirmationAlert(onConfirm) {
        this.ModalDialogService.showDialog({
            modalDialogId: 'BatchTemplateOverrideAlert',
            title: 'MESSAGE_OVERWIRTE_ALERT_HEADER',
            body: 'MESSAGE_OVERWIRTE_ALERT',
            showCancelButton: true,
            showCloseButton: false,
            customZIndex: 2000,
            onClose: () => {
            },
            buttons: [
                {
                    label: 'MESSAGE_OVERWIRTE_ALERT_YES',
                    className: 'primary-button',
                    onClick: () => onConfirm(),
                },
            ],
        });
    }

    onEmailTextareaEditStateChanged(state) {
        if (state !== this.emailTextareaEditState) {
            this.emailTextareaEditState = state;

            if (state === EDIT_STATE) {
                this.mapEmailToTokens();
            } else if (state === VIEW_STATE) {
                this.mapEmailToSpans();
            }
        }
    }

    mapEmailToTokens() {
        const { subject, template } = this.selectedTemplate;

        this.email = {
            subject: MessageFormatter.mapSpanToToken(subject),
            message: MessageFormatter.mapSpanToToken(template),
        };
    }

    mapEmailToSpans() {
        const emailWithSpans = {
            subject: MessageFormatter.mapTokenToSpan(this.email.subject),
            message: MessageFormatter.mapTokenToSpan(this.email.message),
        };

        if (emailWithSpans.message) {
            const { candidates, recruiter, position, employerRep } = this;
            const candidate = candidates[0];
            const emailTemplate = { template: emailWithSpans.message, subject: emailWithSpans.subject };
            const { template, subject } = MessageFormatter.fillTemplate(emailTemplate, candidate, recruiter, position, undefined, employerRep);

            this.email = {
                subject,
                message: template,
            };
            this.selectedTemplate = {
                ...this.selectedTemplate,
                subject: emailWithSpans.subject,
                template: emailWithSpans.message,
            };
        }
    }
}

export const emailModeConfiguration = {
    template,
    controller: EmailModeComponent,
    bindings: {
        candidates: '<',
        recruiter: '<',
        position: '<',
        employerRep: '<',
        onDone: '&',
        onClose: '&',
        onSendEmail: '&',
    },
    controllerAs: 'emailModeCtrl',
};

angular.module('TALENYA-FE').component('emailMode', emailModeConfiguration);
